"use client";

import React, { useEffect, useRef, useState } from "react";

import Image from "next/image";

import { DefaultNeedIcon } from "@/components/icons/NeedIcons";
import { NeedUrgency } from "@/lib/types/common";
import {
  getUrgencyBackgroundColor,
  getUrgencyTextColor,
} from "@/lib/urgency-utils";

interface NeedImageProps {
  /**
   * URL of the image to display
   */
  src?: string | null;
  /**
   * Alternative text for the image
   */
  alt: string;
  /**
   * Need data used for the default image when no src is provided
   */
  need: { urgency: NeedUrgency; tags?: { id: string; name: string }[] };
  /**
   * Optional ID for the component
   */
  id?: string;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Whether to fill the container
   */
  fill?: boolean;
  /**
   * Aspect ratio (width:height) - defaults to 16:9 (landscape)
   */
  aspectRatio?: string;
  /**
   * Optional sizes attribute for responsive images
   */
  sizes?: string;
  /**
   * Width of the image (only used when fill=false and no aspectRatio)
   */
  width?: number;
  /**
   * Height of the image (only used when fill=false and no aspectRatio)
   */
  height?: number;
}

// Domains that are whitelisted in next.config.js for Next.js Image optimization
const WHITELISTED_DOMAINS = [
  "images.unsplash.com",
  "images.pexels.com",
  "walmartimages.com",
  "media-amazon.com",
  "ssl-images-amazon.com",
  "amazonaws.com",
  "needbridge.s3.us-east-1.amazonaws.com",
];

const ICON_TYPE_KEYWORDS: [string, string][] = [
  ["baby", "baby"],
  ["infant", "baby"],
  ["child", "child"],
  ["kid", "child"],
  ["family", "family"],
  ["adult", "family"],
];

function getIconTypeFromTags(
  tags: { id: string; name: string }[] | undefined,
): string {
  if (!tags || tags.length === 0) return "general";

  const tagText = tags.map((tag) => tag.name.toLowerCase()).join(" ");
  const match = ICON_TYPE_KEYWORDS.find(([kw]) => tagText.includes(kw));

  return match ? match[1] : "general";
}

function computeContainerStyle(
  fill: boolean,
  aspectRatio: string,
  width: number | undefined,
  height: number | undefined,
): React.CSSProperties {
  if (fill) return { aspectRatio };
  if (aspectRatio) return { aspectRatio, width: "100%" };
  if (width && height) return { height: `${height}px`, width: `${width}px` };

  return { width: "100%" };
}

function computeAspectRatioPadding(aspectRatio: string): string {
  return aspectRatio === "16:9" ? "56.25%" : "75%";
}

function getTagContext(
  tags: { id: string; name: string }[] | undefined,
): string {
  if (!tags || tags.length === 0) return "General";

  return tags.map((tag) => tag.name).join(", ");
}

function watchImageLoad(
  src: string | null | undefined,
  hasError: boolean,
  imgRef: React.RefObject<HTMLImageElement | null>,
  setIsLoading: (v: boolean) => void,
): (() => void) | undefined {
  if (!isValidUrl(src) || hasError) {
    setIsLoading(false);

    return undefined;
  }

  const timeoutId = setTimeout(() => setIsLoading(false), 5000);
  const checkImageLoaded = () => {
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      setIsLoading(false);
      clearTimeout(timeoutId);
    }
  };

  checkImageLoaded();
  const checkTimeout = setTimeout(checkImageLoaded, 100);

  return () => {
    clearTimeout(timeoutId);
    clearTimeout(checkTimeout);
  };
}

function shouldShowDefault(
  validSrc: string | null | undefined,
  hasError: boolean,
): boolean {
  return !validSrc || hasError;
}

function syncLoadingWithSrc(
  src: string | null | undefined,
  setIsLoading: (v: boolean) => void,
  setHasError: (v: boolean) => void,
) {
  if (isValidUrl(src)) {
    setIsLoading(true);
  } else {
    setIsLoading(false);
  }
  setHasError(false);
}

function isValidUrl(url: string | null | undefined): boolean {
  if (!url || typeof url !== "string" || url.trim() === "") return false;
  try {
    const parsed = new URL(url);

    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Check if an image URL is from a whitelisted domain that supports Next.js Image optimization
 */
const isWhitelistedDomain = (url: string): boolean => {
  try {
    const hostname = new URL(url).hostname;

    return WHITELISTED_DOMAINS.some(
      (domain) => hostname === domain || hostname.endsWith(`.${domain}`),
    );
  } catch {
    return false;
  }
};

/**
 * Need image component with fallback
 *
 * Features:
 * - Handles image loading errors
 * - Provides default image when no src is available
 * - Maintains consistent sizing and aspect ratio
 * - Accessible to screen readers
 * - FIX Bug #4: Uses regular img tag for non-whitelisted domains to allow ANY external URL
 */
const DEFAULT_ASPECT_RATIO = "16:9";
const DEFAULT_SIZES =
  "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw";

function resolveNeedImageProps(props: NeedImageProps) {
  return {
    alt: props.alt,
    aspectRatio: props.aspectRatio ?? DEFAULT_ASPECT_RATIO,
    className: props.className ?? "",
    fill: props.fill ?? false,
    height: props.height,
    id: props.id,
    need: props.need,
    sizes: props.sizes ?? DEFAULT_SIZES,
    src: props.src,
    width: props.width,
  };
}

interface ImageInnerProps {
  alt: string;
  sizes: string;
  validSrc: string | null | undefined;
  isLoading: boolean;
  imgRef: React.RefObject<HTMLImageElement | null>;
  onError: () => void;
  onLoad: () => void;
}

function renderOptimizedImage({
  alt,
  isLoading,
  onError,
  onLoad,
  sizes,
  validSrc,
}: ImageInnerProps) {
  const opacityClass = isLoading ? "opacity-0" : "opacity-100";

  if (!validSrc) return null;

  if (isWhitelistedDomain(validSrc)) {
    return (
      <Image
        alt={alt}
        className={`object-cover transition-opacity duration-300 ${opacityClass}`}
        fill={true}
        sizes={sizes}
        src={validSrc}
        onError={onError}
        onLoad={onLoad}
      />
    );
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      alt={alt}
      className={`object-cover transition-opacity duration-300 w-full h-full ${opacityClass}`}
      src={validSrc}
      onError={onError}
      onLoad={onLoad}
    />
  );
}

export function NeedImage(props: NeedImageProps) {
  const {
    alt,
    aspectRatio,
    className,
    fill,
    height,
    id,
    need,
    sizes,
    src,
    width,
  } = resolveNeedImageProps(props);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const tagContext = getTagContext(need.tags);

  const handleError = () => {
    setHasError(true);
    setIsLoading(false);
  };

  const handleLoad = () => {
    setIsLoading(false);
  };

  useEffect(() => {
    syncLoadingWithSrc(src, setIsLoading, setHasError);
  }, [src]);

  useEffect(() => {
    return watchImageLoad(src, hasError, imgRef, setIsLoading);
  }, [src, hasError]);

  const componentId = id ?? "need-image";
  const containerClasses = `relative overflow-hidden w-full ${className}`;
  const ariaLabel = `Default image for ${tagContext} need with ${need.urgency} urgency`;
  const containerStyle = computeContainerStyle(
    fill,
    aspectRatio,
    width,
    height,
  );
  const aspectRatioPadding = computeAspectRatioPadding(aspectRatio);
  const iconType = getIconTypeFromTags(need.tags);
  const validSrc = isValidUrl(src) ? src : null;
  const showDefault = shouldShowDefault(validSrc, hasError);

  if (showDefault) {
    return (
      <div className={containerClasses} id={componentId} style={containerStyle}>
        <div style={{ paddingBottom: aspectRatioPadding }} />
        <div
          aria-label={ariaLabel}
          className={`absolute inset-0 flex items-center justify-center ${getUrgencyBackgroundColor(need.urgency)}`}
        >
          <DefaultNeedIcon
            className={`w-24 h-24 ${getUrgencyTextColor(need.urgency)}`}
            isDecorative={true}
            type={iconType}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={containerClasses} id={componentId} style={containerStyle}>
      <div style={{ paddingBottom: aspectRatioPadding }} />
      <div className="absolute inset-0">
        {renderOptimizedImage({
          alt,
          imgRef,
          isLoading,
          onError: handleError,
          onLoad: handleLoad,
          sizes,
          validSrc,
        })}
      </div>
      {isLoading && (
        <div
          className={`absolute inset-0 flex items-center justify-center ${getUrgencyBackgroundColor(need.urgency)}`}
        >
          <DefaultNeedIcon
            className={`w-24 h-24 ${getUrgencyTextColor(need.urgency)}`}
            isDecorative={true}
            type={iconType}
          />
        </div>
      )}
    </div>
  );
}
